import React from "react";
import { ErrorMessage } from "formik";
import clsx from "clsx";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  Stack,
  TextField,
  IconButton,
  FormControlLabel,
  InputAdornment,
  ButtonBase,
  Collapse,
} from "@mui/material";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useStyles } from "./styles";

function APIConfigSection({
  values,
  handleChange,
  handleBlur,
  errors,
  touched,
  hasValidationError = false,
  sectionStateKey = null,
}) {
  const classes = useStyles();
  const [showPassword, setShowPassword] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const contentId = "api-type-content";

  React.useEffect(() => {
    setExpanded(false);
  }, [sectionStateKey]);

  React.useEffect(() => {
    if (hasValidationError) {
      setExpanded(true);
    }
  }, [hasValidationError]);

  return (
    <Card variant="outlined" sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
      <CardContent sx={{ pt: 2.5, pb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <ButtonBase
            aria-controls={contentId}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} API Type fields`}
            onClick={() => setExpanded((current) => !current)}
            sx={{
              alignItems: "center",
              borderRadius: 0.5,
              display: "flex",
              flex: 1,
              justifyContent: "flex-start",
              minWidth: 0,
              px: 0.5,
              py: 0.5,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
              API Type
            </Typography>
            {expanded ? (
              <ExpandLessIcon aria-hidden="true" fontSize="small" sx={{ ml: 0.5 }} />
            ) : (
              <ExpandMoreIcon aria-hidden="true" fontSize="small" sx={{ ml: 0.5 }} />
            )}
          </ButtonBase>
          <FormControl>
            <RadioGroup
              onChange={handleChange}
              value={values.apiType}
              name="apiType"
              row
              aria-label="API Type"
            >
              <FormControlLabel value="mews" control={<Radio />} label="Mews" />
              <FormControlLabel value="avvio" control={<Radio />} label="Avvio" />
              <FormControlLabel
                value="noReservation"
                control={<Radio />}
                label="No Reservation"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box id={contentId} sx={{ mt: 2 }}>
              <Grid container spacing={0.875}>
                {values.apiType === "mews" && (
                  <>
                    <Grid item xs={12}>
                      <Stack>
                        <TextField
                          label="Client ID"
                          name="mewsClientId"
                          className={clsx(
                            classes.textfield,
                            errors.mewsClientId && touched.mewsClientId
                              ? classes.inputError
                              : classes.inputNoError
                          )}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.mewsClientId}
                        />
                        <ErrorMessage
                          name="mewsClientId"
                          component="span"
                          className={classes.error}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack>
                        <TextField
                          label="Client Token"
                          name="clientToken"
                          className={clsx(
                            classes.textfield,
                            errors.clientToken && touched.clientToken
                              ? classes.inputError
                              : classes.inputNoError
                          )}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.clientToken}
                        />
                        <ErrorMessage
                          name="clientToken"
                          component="span"
                          className={classes.error}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack>
                        <TextField
                          label="Access Token"
                          name="accessToken"
                          className={clsx(
                            classes.textfield,
                            errors.accessToken && touched.accessToken
                              ? classes.inputError
                              : classes.inputNoError
                          )}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.accessToken}
                        />
                        <ErrorMessage
                          name="accessToken"
                          component="span"
                          className={classes.error}
                        />
                      </Stack>
                    </Grid>
                  </>
                )}

                {values.apiType === "avvio" && (
                  <>
                    <Grid item xs={12}>
                      <Stack>
                        <TextField
                          label="Username"
                          name="avvioUsername"
                          autoComplete="off"
                          className={clsx(
                            classes.textfield,
                            errors.avvioUsername && touched.avvioUsername
                              ? classes.inputError
                              : classes.inputNoError
                          )}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.avvioUsername}
                        />
                        <ErrorMessage
                          name="avvioUsername"
                          component="span"
                          className={classes.error}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack>
                        <TextField
                          label="Password"
                          name="avvioPassword"
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          className={clsx(
                            classes.textfield,
                            errors.avvioPassword && touched.avvioPassword
                              ? classes.inputError
                              : classes.inputNoError
                          )}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.avvioPassword}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  type="button"
                                  aria-label="toggle password visibility"
                                  onClick={() => setShowPassword((current) => !current)}
                                  edge="end"
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <ErrorMessage
                          name="avvioPassword"
                          component="span"
                          className={classes.error}
                        />
                      </Stack>
                    </Grid>
                  </>
                )}

                {values.apiType === "noReservation" && (
                  <Grid item xs={12}>
                    <Stack>
                      <TextField
                        label="Hotel Redirect URL"
                        name="hotelRedirectUrl"
                        className={clsx(
                          classes.textfield,
                          errors.hotelRedirectUrl && touched.hotelRedirectUrl
                            ? classes.inputError
                            : classes.inputNoError
                        )}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.hotelRedirectUrl}
                      />
                      <ErrorMessage
                        name="hotelRedirectUrl"
                        component="span"
                        className={classes.error}
                      />
                    </Stack>
                  </Grid>
                )}
              </Grid>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default APIConfigSection;
